import React, { useEffect } from 'react';
import { initHolid } from './holid-ad-helper';
import { useAppSelector } from 'tradera/state/hooks';
import { selectIsTcfLoaded } from 'tradera/state/ads/selectors';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { useOnMount } from 'tradera/hooks/use-on-mount';
import { isServer } from 'tradera/utils/nextjs';
import type { AdFormat } from './ad-format';
import { selectMember } from 'tradera/state/member/selectors';

type WindowWithHolidKeyValues = Window & {
    holid_keyvalues: {
        // age
        kvag?: string;
        // gender
        kvge?: string;
        // category
        kvca?: string;
    };
};

declare const window: WindowWithHolidKeyValues;

const DEBUG = !isServer && window.location.search.includes('debug-ads');

const log = (...args: string[]) => {
    if (!DEBUG) {
        return;
    }
    console.log('holid-ad: ', ...args);
};

const useSetupHolidGlobals = () => {
    const { age, gender } = useAppSelector(selectMember);

    const { categoryPath } = useAppSelector((state) => state.discover);
    const discoverCategory = categoryPath?.find(
        (category) => category.isTopLevel
    );

    const { breadCrumbs } = useAppSelector((state) => state.views.viewItem);
    const vipCategory = breadCrumbs?.[0];

    const category = vipCategory?.name || discoverCategory?.name;

    useEffect(() => {
        window.holid_keyvalues = {};

        if (age) {
            window.holid_keyvalues.kvag = age > 75 ? '75' : age.toString();
        }

        if (gender) window.holid_keyvalues.kvge = gender;

        if (category) {
            // key-value entries entry can not contain certain special characters, see:
            // https://support.google.com/admanager/answer/10020177?hl=en&ref_topic=10019298&sjid=1638882297201713048-EU
            // eslint-disable-next-line no-useless-escape
            const regexp = /["\'=!+#*~;^()<>[\],&]/g;
            const cleanedCategory = category.replace(regexp, '');
            window.holid_keyvalues.kvca = cleanedCategory;
        }
    }, [age, gender, category]);
};

export type HolidAdProps = {
    adFormat: AdFormat;
    adSize: [number, number];
};

const HolidAd = ({ adFormat, adSize }: HolidAdProps) => {
    const isHolidBetaTest = useIsFeatureEnabled('ads-holid-beta-test');
    const isTcfLoaded = useAppSelector(selectIsTcfLoaded);

    useOnMount(() => {
        log(
            `mounted 
            isHolidBetaTest=${isHolidBetaTest} 
            isTcfLoaded=${isTcfLoaded} 
            adFormat=${adFormat}`
        );

        return () => log('UNMOUNTED');
    });

    useSetupHolidGlobals();

    useEffect(() => {
        if (isTcfLoaded) {
            initHolid(isHolidBetaTest);
        }
    }, [isHolidBetaTest, isTcfLoaded]);

    return (
        <div
            className={`holidAds ${adFormat} h-100 overflow-hidden`}
            style={{
                width: adSize[0],
                height: adSize[1]
            }}
        />
    );
};

export default HolidAd;
